import { FISH_RARITY, FISH_RARITY_COLORS, ITEM_NAMES, isFishItem, type FishKind } from "@ghostland/shared";
import { drawItemIcon } from "../items/itemArt.js";

/**
 * Centered "fish caught!" popup: a pixel fishing-rod-with-fish icon, then the fish icon + name
 * + size fade in below. Auto-dismisses. Driven by the server FishCaught message.
 */

const STYLE_ID = "gl-fishcatch-style";
const CSS = `
#gl-fishcatch {
  position: fixed; inset: 0; z-index: 55; display: none; align-items: center; justify-content: center;
  pointer-events: none;
}
#gl-fishcatch.open { display: flex; }
#gl-fishcatch .card {
  background: #0a0a0a; border: 2px solid #f4f4f4; box-shadow: 0 0 0 2px #000, 0 10px 40px rgba(0,0,0,.6);
  padding: 18px 26px 20px; text-align: center; min-width: 210px;
  font-family: ui-monospace, monospace; image-rendering: pixelated;
  animation: gl-fc-pop .22s cubic-bezier(.2,1.5,.4,1) both;
}
@keyframes gl-fc-pop { from { transform: scale(.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
#gl-fishcatch .rod { width: 112px; height: 112px; margin: 0 auto; display: block; image-rendering: pixelated; }
#gl-fishcatch .catch { margin-top: 6px; opacity: 0; transform: translateY(6px); transition: opacity .4s ease, transform .4s ease; }
#gl-fishcatch .catch.show { opacity: 1; transform: translateY(0); }
#gl-fishcatch .catch canvas.fish { width: 48px; height: 48px; image-rendering: pixelated; display: block; margin: 0 auto 4px; }
#gl-fishcatch .catch .hdr { font-size: 9px; letter-spacing: 2px; color: #9aa4ae; margin-bottom: 3px; }
#gl-fishcatch .catch .name { font-size: 14px; font-weight: 700; letter-spacing: 1px; }
#gl-fishcatch .catch .size { font-size: 12px; color: #7ce0a0; margin-top: 2px; }
#gl-fishcatch.out .card { animation: gl-fc-out .3s ease-in both; }
@keyframes gl-fc-out { to { transform: scale(.9); opacity: 0; } }
`;

let overlay: HTMLDivElement | null = null;
let dismissTimer: number | null = null;
let revealTimer: number | null = null;

function ensure(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "gl-fishcatch";
    document.body.appendChild(overlay);
  }
}

/** Show the catch popup for `kind` at `size` inches. */
export function showFishCatch(kind: string, size: number): void {
  ensure();
  if (!overlay) return;
  if (dismissTimer !== null) { window.clearTimeout(dismissTimer); dismissTimer = null; }
  if (revealTimer !== null) { window.clearTimeout(revealTimer); revealTimer = null; }

  const isFish = isFishItem(kind);
  const color = isFish ? FISH_RARITY_COLORS[FISH_RARITY[kind as FishKind]] : "#f4f4f4";
  const name = ITEM_NAMES[kind as keyof typeof ITEM_NAMES] ?? kind;

  overlay.className = "open";
  overlay.innerHTML = "";
  const card = document.createElement("div");
  card.className = "card";

  const rod = document.createElement("img");
  rod.className = "rod";
  rod.src = "/assets/fishcatch/rod.png";
  rod.alt = "";
  card.appendChild(rod);

  const catchEl = document.createElement("div");
  catchEl.className = "catch";
  const fish = drawItemIcon(kind);
  fish.className = "fish";
  catchEl.appendChild(fish);
  const hdr = document.createElement("div"); hdr.className = "hdr"; hdr.textContent = "YOU CAUGHT";
  const nm = document.createElement("div"); nm.className = "name"; nm.textContent = name; nm.style.color = color;
  const sz = document.createElement("div"); sz.className = "size"; sz.textContent = `${size.toFixed(2)}″`;
  catchEl.append(hdr, nm, sz);
  card.appendChild(catchEl);

  overlay.appendChild(card);

  // reel for a beat, then fade the fish + size in
  revealTimer = window.setTimeout(() => { catchEl.classList.add("show"); revealTimer = null; }, 450);
  // auto-dismiss
  dismissTimer = window.setTimeout(() => {
    overlay?.classList.add("out");
    window.setTimeout(() => { overlay?.classList.remove("open"); overlay?.classList.remove("out"); }, 300);
    dismissTimer = null;
  }, 2600);
}
